/**
 * ComposerAttachmentsContext
 *
 * One per conversation. Holds the in-flight + completed attachment list
 * the composer renders as chips above the input, plus the bridge that
 * the runtime adapter uses to attach `fileRefs` to the next
 * `invokeAsync` call.
 *
 * Files upload immediately when added (drop or `+`). Send doesn't gate
 * on uploads — instead, `consume()` is async and resolves once every
 * current job has reached a terminal status. The adapter awaits it
 * before calling `invokeAsync`, so the agent never sees a `fileRef`
 * pointing at a file that hasn't fully landed in the VFS yet.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useVfsUpload } from '@/hooks/useVfsUpload';
import { useVfsFiles } from '@/hooks/useVfsFiles';
import { resolveCollisionFreePath } from '@/lib/vfs-collision';
import { MAX_DROPPED_FILES } from '@/lib/dropped-entries';
import { useToast } from '@/components/ui/Toast';
import type { FileRef } from '@/types';
import type { VfsEntry } from '@/types/vfs';

export type AttachmentStatus = 'pending' | 'uploading' | 'completing' | 'done' | 'error';

export interface AttachmentJob {
  id: string;
  file: File;
  /** Resolved VFS path after collision check (e.g. "uploads/data-2.csv"). */
  path: string;
  /** Display basename (matches the last segment of `path`). */
  name: string;
  size: number;
  contentType: string;
  status: AttachmentStatus;
  /** 0..1 — only meaningful while `status === 'uploading'`. */
  progress: number;
  fileId?: string;
  error?: string;
}

export interface ComposerAttachmentsCtx {
  attachments: AttachmentJob[];
  addFiles: (files: File[]) => void;
  remove: (id: string) => void;
  /** Awaits every in-flight job, returns the FileRefs for the done ones,
   *  clears the consumed entries. Drops errored / cancelled jobs. */
  consume: () => Promise<FileRef[]>;
  hasInFlightUploads: boolean;
}

const Ctx = createContext<ComposerAttachmentsCtx | null>(null);

/** Returns the live attachments context, or `null` if no provider is mounted.
 *  Composer + runtime adapter both call this; both gracefully handle null
 *  so the feature can be flag-gated or omitted from non-chat surfaces. */
export function useComposerAttachments(): ComposerAttachmentsCtx | null {
  return useContext(Ctx);
}

interface ProviderProps {
  /** VFS owner — conversation id is the natural value. Uploads land here. */
  ownerId: string | undefined;
  /** Folder prefix for uploaded files. Defaults to "uploads". */
  prefix?: string;
  children: ReactNode;
}

export function ComposerAttachmentsProvider({
  ownerId,
  prefix = 'uploads',
  children,
}: ProviderProps) {
  const [jobs, setJobs] = useState<AttachmentJob[]>([]);
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;

  // Used to detect collisions against the live VFS state — picks `data-2.csv`
  // when `data.csv` already exists. Best-effort: relies on whatever
  // `useVfsFiles` has loaded; a race with a concurrent agent write is
  // possible but rare.
  const { entries } = useVfsFiles(ownerId);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // Single instance — we read `jobs` for progress polling and call
  // uploadFile / cancel for the state-machine drive.
  const vfsUpload = useVfsUpload(ownerId);
  const { uploadFile, cancel: cancelUpload } = vfsUpload;
  const vfsJobsRef = useRef(vfsUpload.jobs);
  vfsJobsRef.current = vfsUpload.jobs;

  const toast = useToast();

  // Pending consumers waiting for all jobs to settle.
  const waitersRef = useRef<Array<() => void>>([]);

  const allSettled = useCallback((list: AttachmentJob[]) =>
    list.every((j) => j.status === 'done' || j.status === 'error'),
  []);

  // Whenever jobs settle, drain any consume() awaiters.
  useEffect(() => {
    if (waitersRef.current.length === 0) return;
    if (!allSettled(jobs)) return;
    const waiters = waitersRef.current;
    waitersRef.current = [];
    for (const fn of waiters) fn();
  }, [jobs, allSettled]);

  const updateJob = useCallback((id: string, patch: Partial<AttachmentJob>) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
  }, []);

  const addFiles = useCallback(
    (files: File[]) => {
      if (!ownerId || files.length === 0) return;

      // Total-cap guard — covers the file-picker path (`<input type=file>`)
      // and any direct caller. Drops that arrive via the dropzone are
      // already rejected upstream by readDroppedEntries; this is the
      // safety net for everyone else. All-or-nothing: we either add the
      // whole batch or reject it.
      const currentCount = jobsRef.current.length;
      if (currentCount + files.length > MAX_DROPPED_FILES) {
        toast.error(
          `You can attach up to ${MAX_DROPPED_FILES} files per message.`,
        );
        return;
      }

      // Reserve resolved paths in a local set so two files dropped in the
      // same batch don't pick the same suffix.
      const reserved = new Set<string>([
        ...entriesRef.current.map((e) => e.path),
        ...jobsRef.current.map((j) => j.path),
      ]);

      const newJobs: AttachmentJob[] = [];
      for (const file of files) {
        const { name, path } = resolveCollisionFreePath(file.name, prefix, reserved);
        reserved.add(path);
        newJobs.push({
          id: makeLocalId(),
          file,
          path,
          name,
          size: file.size,
          contentType: file.type || 'application/octet-stream',
          status: 'pending',
          progress: 0,
        });
      }

      setJobs((prev) => [...prev, ...newJobs]);

      // Fire all uploads in parallel.
      for (const job of newJobs) {
        void runUpload(job);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ownerId, prefix, toast],
  );

  // Drives one job through the upload state machine. Keep the live `jobs`
  // shape in sync by reading XHR progress from the underlying useVfsUpload
  // (which exposes its own jobs[] — we mirror updates here).
  const runUpload = useCallback(
    async (job: AttachmentJob) => {
      try {
        updateJob(job.id, { status: 'uploading' });

        // Start a progress poller — useVfsUpload's internal jobs[] has the
        // XHR-driven progress; mirror it into our richer AttachmentJob.
        // The Vfs hook keys by path so we can look up by job.path.
        let lastProgress = 0;
        const progressTimer = window.setInterval(() => {
          const inner = vfsJobsRef.current.find((j) => j.path === job.path);
          if (!inner) return;
          if (inner.progress !== lastProgress) {
            lastProgress = inner.progress;
            updateJob(job.id, { progress: inner.progress });
          }
          if (inner.status === 'completing') {
            updateJob(job.id, { status: 'completing', progress: 1 });
          }
        }, 120);

        try {
          const entry = await uploadFile(job.file, { prefix, name: job.name });
          if (!entry) {
            updateJob(job.id, { status: 'error', error: 'Upload failed' });
            return;
          }
          updateJob(job.id, {
            status: 'done',
            progress: 1,
            fileId: entry.fileId,
            // Trust the backend's contentType if it normalized ours.
            contentType: entry.contentType || job.contentType,
            size: entry.size || job.size,
          });
        } finally {
          window.clearInterval(progressTimer);
        }
      } catch (err) {
        updateJob(job.id, {
          status: 'error',
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [prefix, updateJob, uploadFile],
  );

  const remove = useCallback((id: string) => {
    const job = jobsRef.current.find((j) => j.id === id);
    if (!job) return;
    if (job.status === 'uploading' || job.status === 'pending') {
      cancelUpload(job.path);
    }
    setJobs((prev) => prev.filter((j) => j.id !== id));
  }, [cancelUpload]);

  const consume = useCallback(async (): Promise<FileRef[]> => {
    // Wait until every current job is settled. New jobs added while we wait
    // get included because we re-evaluate on each state change.
    if (!allSettled(jobsRef.current)) {
      await new Promise<void>((resolve) => {
        waitersRef.current.push(resolve);
      });
    }
    const settled = jobsRef.current;
    const refs: FileRef[] = settled
      .filter((j) => j.status === 'done')
      .map((j) => ({
        path: j.path,
        name: j.name,
        size: j.size,
        contentType: j.contentType,
        ...(j.fileId ? { fileId: j.fileId } : {}),
      }));
    // Clear all consumed jobs (drops both done + error from the chip row).
    setJobs([]);
    return refs;
  }, [allSettled]);

  const hasInFlightUploads = useMemo(
    () => jobs.some((j) => j.status === 'pending' || j.status === 'uploading' || j.status === 'completing'),
    [jobs],
  );

  const value = useMemo<ComposerAttachmentsCtx>(
    () => ({ attachments: jobs, addFiles, remove, consume, hasInFlightUploads }),
    [jobs, addFiles, remove, consume, hasInFlightUploads],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ─── Helpers ──────────────────────────────────────────────────────────

function makeLocalId(): string {
  return `att-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// Silences unused-warning for `VfsEntry` import in case the type is only
// referenced in JSDoc; cheap and self-documenting.
export type { VfsEntry };
